import { useState } from "react";

import { Modal } from "@smart/itops-sb-design-system-dom";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";
import { useUpdateField } from "@smart/manage-gql-client-dom";
import { GqlFieldValues } from "@smart/manage-gql-operations-dom/src/generated/types";

import {
  buildFixedSelectionForGroup,
  getGroupUpdateOptions,
  mapMatterField,
} from "./helpers";
import { useMappedMatterFields } from "./hooks";
import { Picker } from "./picker";
import { PickerSelection } from "./types";
import {
  GqlField,
  GqlGroup,
  GqlMatterLayout,
  GqlMatterType,
} from "../../../types";
import { fieldComponentIcons } from "../../constants";
import { GroupOptions, LoadMatterFields } from "../../types";

export type MappedFieldsModalProps = {
  onClose: () => void;
  updateField: ReturnType<typeof useUpdateField>[0];
  updateExistingGroupForLayoutContact: (group: GroupOptions) => Promise<void>;
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  fields: GqlField[];
  groups: GqlGroup[];
  loadMatterFields: LoadMatterFields;
  selectedFieldToMap: GqlFieldValues | undefined;
  loading?: boolean;
};

export const ChangeMappedFieldTypeModal = ({
  selectedFieldToMap,
  onClose,
  matterLayouts,
  matterTypes,
  fields,
  groups,
  loadMatterFields,
  updateField,
  updateExistingGroupForLayoutContact,
  loading: dataLoading,
}: MappedFieldsModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const selectedFieldGroup = groups.find(
    (g) => g.uri === selectedFieldToMap?.groupUri,
  );

  const buildSelectionForGroup = (): PickerSelection | undefined => {
    if (!selectedFieldGroup?.values) return undefined;

    const selectedGroup = selectedFieldGroup.values;

    return buildFixedSelectionForGroup({
      layout:
        (selectedGroup.layout as GqlMatterLayout | undefined) ||
        (selectedFieldToMap?.layout as GqlMatterLayout | undefined),
      groupField: selectedGroup.field,
      fallbackField: selectedFieldToMap?.field,
    });
  };

  const selectionForGroup = buildSelectionForGroup();

  const initial: PickerSelection = {
    layout: selectionForGroup?.layout,
    prefix: selectionForGroup?.prefix || [],
    fields: [],
    components: selectionForGroup?.components || [],
  };

  const [selection, setSelection] = useState<PickerSelection>(initial);

  const {
    isLoading: isLoadingMatterFields,
    matterFields,
    existingMappedFields,
    currentLayoutComponent,
  } = useMappedMatterFields({
    selection,
    fields,
    matterTypes,
    groups,
    destinationGroup: selectedFieldGroup,
    loadMatterFields,
  });

  const onSaveChanges = async () => {
    setIsSaving(true);

    try {
      if (selection.fields.length && selection.layout && selectedFieldToMap) {
        const group = getGroupUpdateOptions({
          selection,
          currentLayoutComponent,
          groupUri: selectedFieldGroup?.uri,
        });

        if (group?.layout && group?.field && group?.uri) {
          await updateExistingGroupForLayoutContact(group);
        }

        const newField = selection.fields
          .map(mapMatterField(selection.layout))
          .filter(isNotNullOrUndefined)[0];

        await updateField({
          variables: {
            uri: selectedFieldToMap.uri,
            sectionUri: selectedFieldToMap.sectionUri,
            formUri: selectedFieldToMap.formUri,
            order: selectedFieldToMap.order,
            groupUri: selectedFieldToMap.groupUri || undefined,
            fields: {
              label: selectedFieldToMap.label,
              hint: selectedFieldToMap.hint,
              options: newField.options || [],
              supportsCustomValues: newField.supportsCustomValues,
              mandatory: selectedFieldToMap.mandatory,
              allowCustomResponse: newField.allowCustomResponse,
              layout: newField.layout,
              field: newField.field,
              type: newField.type,
              links: undefined,
            },
          },
        });
      }
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
      setSelection(initial);
    }
  };

  return (
    <Modal
      className="[&_.modal-container]:min-h-[min(90rem,95vh)]"
      open={!!selectedFieldToMap}
      onClose={() => {
        setSelection(initial);
        onClose();
      }}
      loading={isSaving}
      header={{
        icon: fieldComponentIcons.mapped.name,
        iconBgColor: fieldComponentIcons.mapped.bgColor,
        text: "Select a mapped field",
      }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: () => {
              setSelection(initial);
              onClose();
            },
          },
          {
            text: `Update field`,
            variant: "primaryDefault",
            disabled:
              !selection.layout ||
              !selection.fields.length ||
              isLoadingMatterFields ||
              dataLoading ||
              isSaving,
            onClick: onSaveChanges,
          },
        ],
      }}
    >
      <Picker
        loading={dataLoading || isLoadingMatterFields || false}
        isLoadingMatterFields={isLoadingMatterFields}
        selection={selection}
        setSelection={setSelection}
        matterTypes={matterTypes || []}
        layouts={matterLayouts || []}
        fields={matterFields}
        existingMappedFields={existingMappedFields}
        multiSelect={false}
        fixedSelection={selectionForGroup}
        group={selectedFieldGroup}
        groups={groups}
        allowLayoutContactFields={
          selectedFieldGroup?.values.type === "layoutContact"
        }
      />
    </Modal>
  );
};
